import { Stack } from "@mui/material";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useOpenDocumentMutation } from "web_myplann/Api/Document/useOpenDocumentMutation";
import {
	type ClientPortfolioDetailRelatedResponseDTO,
	ClientPortfolioDomainEnum,
	type PortfolioDocumentListItemDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getRelatedPropertyTitle } from "web_myplann/Portfolio/Helpers/getRelatedPropertyTitle";
import { ActionRow } from "web_myplann/Shared/Components/ActionRow";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";

const RelatedSectionType = {
	Documents: "documents",
	Contracts: "contracts",
	Properties: "properties",
	Services: "services",
} as const;

type Props = {
	related: ClientPortfolioDetailRelatedResponseDTO[];
	documents: PortfolioDocumentListItemDTO[] | undefined;
	portfolioId: number;
};

const isServiceOrAdvisoryDomain = (
	domain: ClientPortfolioDomainEnum | undefined,
) => {
	return (
		domain === ClientPortfolioDomainEnum.Services ||
		domain === ClientPortfolioDomainEnum.PaidAdvisory
	);
};

export const RelatedSection: FC<Props> = ({ related, documents = [] }) => {
	const navigate = useNavigate();
	const { t } = useResource();
	const { mutate: openDocument, isPending: isOpeningDocument } =
		useOpenDocumentMutation();

	const handleOpenDocument = (document: PortfolioDocumentListItemDTO) => {
		if (isOpeningDocument) {
			return;
		}
		openDocument({ id: document.id, fallbackTitle: document.title });
	};

	const hasDocuments = !!documents && documents.length > 0;
	const hasRelatedContracts =
		!!related &&
		related.some(
			(r) =>
				r.relatedPortfolio &&
				!isServiceOrAdvisoryDomain(r.relatedPortfolio.domain),
		);
	const hasRelatedProperties =
		!!related && related.some((r) => r.relatedProperty);
	const hasRelatedServices =
		!!related &&
		related.some(
			(r) =>
				r.relatedPortfolio?.domain &&
				isServiceOrAdvisoryDomain(r.relatedPortfolio.domain),
		);

	const lastSection =
		(hasRelatedServices && RelatedSectionType.Services) ||
		(hasRelatedProperties && RelatedSectionType.Properties) ||
		(hasRelatedContracts && RelatedSectionType.Contracts) ||
		(hasDocuments && RelatedSectionType.Documents) ||
		null;

	return (
		<>
			{hasDocuments && (
				<DataCard
					preset={DataCardPreset.Documents}
					noDivider={lastSection === RelatedSectionType.Documents}
				>
					<Stack gap={0.4}>
						{documents.map((document) => (
							<ActionRow
								key={document.id}
								truncate
								label={document.title}
								onClick={() => handleOpenDocument(document)}
							/>
						))}
					</Stack>
				</DataCard>
			)}
			{related && related.length > 0 && (
				<>
					{hasRelatedContracts && (
						<DataCard
							preset={DataCardPreset.RelatedContracts}
							noDivider={lastSection === RelatedSectionType.Contracts}
						>
							<Stack gap={0.4}>
								{related.map(
									(related) =>
										related.relatedPortfolio &&
										!isServiceOrAdvisoryDomain(
											related.relatedPortfolio.domain,
										) && (
											<ActionRow
												key={related.id}
												truncate
												label={
													related.relatedPortfolio?.name ??
													(() => {
														const product = related.relatedPortfolio?.product;
														const contractNumber =
															related.relatedPortfolio?.contractNumber;

														if (product && contractNumber) {
															return `${product} (${contractNumber})`;
														}

														if (product || contractNumber) {
															return product || contractNumber || "";
														}

														return t(
															Resources.Portfolio.List.PortfolioDomainType[
																related.relatedPortfolio.domain
															],
														);
													})()
												}
												onClick={() =>
													navigate(
														Routing.Portfolio.PortfolioDetail.path(
															related.relatedPortfolio?.myplannPortfolioId?.toString() ??
																"",
														),
													)
												}
											/>
										),
								)}
							</Stack>
						</DataCard>
					)}
					{related.some((r) => r.relatedProperty) && (
						<DataCard
							preset={DataCardPreset.RelatedProperties}
							noDivider={lastSection === RelatedSectionType.Properties}
						>
							<Stack gap={0.4}>
								{related.map(
									(related) =>
										related.relatedProperty && (
											<ActionRow
												key={related.id}
												truncate
												label={getRelatedPropertyTitle(
													related.relatedProperty,
													t,
												)}
												onClick={() =>
													navigate(
														Routing.Portfolio.PropertyDetail.path(
															related?.relatedProperty?.myplannPropertyId?.toString() ??
																"",
														),
													)
												}
											/>
										),
								)}
							</Stack>
						</DataCard>
					)}
					{hasRelatedServices && (
						<DataCard
							preset={DataCardPreset.RelatedServices}
							noDivider={lastSection === RelatedSectionType.Services}
						>
							<Stack gap={0.4} pb={1}>
								{related.map(
									(related) =>
										related.relatedPortfolio?.domain &&
										isServiceOrAdvisoryDomain(
											related.relatedPortfolio.domain,
										) && (
											<ActionRow
												key={related.id}
												truncate
												label={
													related.relatedPortfolio?.name ??
													(() => {
														const product = related.relatedPortfolio?.product;
														const contractNumber =
															related.relatedPortfolio?.contractNumber;

														if (product && contractNumber) {
															return `${product} (${contractNumber})`;
														}

														return product || contractNumber || "";
													})()
												}
												onClick={() =>
													navigate(
														Routing.Portfolio.PortfolioDetail.path(
															related.relatedPortfolio?.myplannPortfolioId?.toString() ??
																"",
														),
													)
												}
											/>
										),
								)}
							</Stack>
						</DataCard>
					)}
				</>
			)}
		</>
	);
};
